import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { resolveEspmSelectionFromIds } from "../../components/Shared/espm";
import { ScreeningRunHeader } from "../../components/Shared/ScreeningRunHeader";
import { ZeosInput } from "../../components/Shared/ZeosInput";
import { ZeosLabel } from "../../components/Shared/ZeosLabel";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import { PropertyForm } from "../../forms/PropertyForm";
import { useEspmPropertyTypeTaxonomy } from "../../hooks/useEspmPropertyTypeTaxonomy";
import { useFormValidation } from "../../hooks/useFormValidation";
import { defaultPropertyForm } from "../../services/forms/DefaultFormData";
import { IPropertyFormDataType } from "../../services/forms/FormDataTypes";
import { PropertyValidationSchema } from "../../services/forms/validationSchemas/PropertyValidationSchema";
import {
  mapPropertyContractToFormData,
  mapPropertyFormToContract,
  triStateFactToBoolean,
} from "../../services/helpers/PropertyHelpers";
import { createLogger } from "../../services/Logger";
import { IListItemSimple } from "../../services/zeos-api/contracts/ListUtility";
import {
  IScreeningRunHistoryItem,
  IScreeningRunSummary,
} from "../../services/zeos-api/contracts/Screening";
import { ZeosListEnum, ZeosListOptionEnum } from "../../services/zeos-api/enums/ListUtility";
import { DashboardFooter } from "./layout/DashboardFooter";
import { DashboardHeader } from "./layout/DashboardHeader";
import {
  buildRunStatusMessage,
  buildScreeningCharacteristics,
  buildScreeningIntake,
  findStageByKey,
  findTraceByKey,
  getChecklistStatusTone,
  humanizeStageKey,
  isTerminalRunStatus,
  normalizeStageKey,
  parseOutputDetail,
  parseStageReason,
  RUN_HISTORY_TAKE,
  RUN_POLL_INTERVAL_SECONDS,
  screeningStageDefinitions,
  toApiReportFormat,
} from "./Screening";

// QA Screening Sandbox: pairs the full Edit-Property field set with a re-run/results flow that
// never touches Common__Property. Reuses PropertyForm (the same fields the real Edit Property
// modal shows) and the pure run/stage-formatting helpers exported from Screening.tsx; deliberately
// duplicates the run-creation/poll/results wiring rather than sharing it, to keep this QA-only
// screen isolated from the production screening entry point.

type ReportFormat = "pdf" | "docx";

const reportFormats: { value: ReportFormat; label: string }[] = [
  { value: "pdf", label: "Export PDF" },
  { value: "docx", label: "Export Word" },
];

export const ScreeningSandbox = () => {
  const api = useContext(ZeosApiContext);
  const log = useMemo(() => createLogger("ScreeningSandbox"), []);
  const [searchParams] = useSearchParams();
  const propertyId = Number(searchParams.get("propertyId")) || null;

  const { formData, setFormData, errors, formValidate, formChange } =
    useFormValidation<IPropertyFormDataType>(PropertyValidationSchema, defaultPropertyForm);

  const [isLoadingProperty, setIsLoadingProperty] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hvacOptions, setHvacOptions] = useState<IListItemSimple[]>([]);
  const [priorWorkTypeOptions, setPriorWorkTypeOptions] = useState<
    { id: number; name: string }[]
  >([]);

  const [liveRun, setLiveRun] = useState<IScreeningRunSummary | null>(null);
  const [runStatus, setRunStatus] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [, setPollCountdown] = useState(0);
  const [runHistory, setRunHistory] = useState<IScreeningRunHistoryItem[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyStatus, setHistoryStatus] = useState<string | null>(null);
  const [isLoadingHistoryRun, setIsLoadingHistoryRun] = useState(false);
  const [expandedStageKeys, setExpandedStageKeys] = useState<string[]>([]);
  const [exportError, setExportError] = useState<string | null>(null);

  // QA-only narrative inputs -- not Edit-Property fields, so they stay out of formData/
  // propertyProfileOverride and never reach Common__Property. They exist so this screen can
  // send the same intakeText/intakeJson narrative the portfolio Screening flow sends (see
  // buildScreeningIntake in Screening.tsx), since the eligibility engine reads that narrative
  // text for signals structured fields alone don't capture.
  const [economicObjectives, setEconomicObjectives] = useState("");
  const [lastRetrofitYear, setLastRetrofitYear] = useState("");
  const [companyName, setCompanyName] = useState<string | null>(null);
  const { categories: espmCategories } = useEspmPropertyTypeTaxonomy();

  const realRunId = liveRun?.runId ?? liveRun?.screeningRunId ?? null;
  const realRunGuid = liveRun?.runGuid ?? null;

  // One-time snapshot of the property's currently saved values -- never re-fetched, and this
  // screen never calls Property/update or Property/create, so edits here are structurally
  // incapable of reaching Common__Property.
  useEffect(() => {
    if (!propertyId) {
      return;
    }
    const loadPropertyAsync = async () => {
      setIsLoadingProperty(true);
      setLoadError(null);
      setEconomicObjectives("");
      setLastRetrofitYear("");
      setCompanyName(null);
      try {
        const response = await api.Property.getProperty(propertyId);
        if (!response?.data) {
          throw new Error(response?.message || "Property not found.");
        }
        setFormData(mapPropertyContractToFormData(response.data, response.data.companyId));
        try {
          const companyResponse = await api.Company.getCompanyAsync(response.data.companyId);
          setCompanyName(companyResponse?.data?.companyName ?? null);
        } catch (error) {
          // Non-critical -- company name just won't appear in the intake narrative.
          log.error("Failed to load company name", error);
        }
      } catch (error: any) {
        log.error("Failed to load property", error);
        setLoadError(error?.message || "Failed to load property.");
      } finally {
        setIsLoadingProperty(false);
      }
    };
    loadPropertyAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  useEffect(() => {
    const loadHvacOptions = async () => {
      try {
        const response = await api.ListUtility.getListAsync(
          ZeosListEnum.HvacPrimaryType,
          ZeosListOptionEnum.excludeUtility
        );
        setHvacOptions(response.data ?? []);
      } catch (error) {
        log.error("Failed to load HVAC system type options", { error });
      }
    };
    loadHvacOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadPriorWorkTypeOptions = async () => {
      try {
        const response = await api.ListUtility.getListAsync(ZeosListEnum.BuildingPriorWorkType);
        setPriorWorkTypeOptions(
          response.data?.map((item) => ({ id: item.dataKey, name: item.display })) ?? []
        );
      } catch (error) {
        log.error("Failed to load prior work type options", { error });
      }
    };
    loadPriorWorkTypeOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRunHistory = async () => {
    if (!propertyId) {
      return;
    }
    setIsHistoryLoading(true);
    setHistoryStatus(null);
    try {
      const response = await api.Screening.getRunHistoryAsync({
        propertyId,
        serviceProviderId: 1,
        take: RUN_HISTORY_TAKE,
      });
      setRunHistory(response?.data || []);
    } catch (error: any) {
      setHistoryStatus(error?.message || "Unable to load run history.");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadRunHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  // Mirrors Screening.tsx's loadRunByGuid -- lets a tester click a past run and see its full
  // results (agentic checklist, run header, all export buttons) without re-running screening.
  // Unlike the production flow, this never touches formData/mode: the sandbox form always
  // reflects the current property snapshot, not a specific past run's inputs.
  const loadHistoryRun = async (runGuid: string) => {
    if (!runGuid) {
      return;
    }
    setIsLoadingHistoryRun(true);
    setRunStatus("Loading saved screening run...");
    try {
      const response = await api.Screening.getRunByGuidAsync(runGuid);
      if (!response?.data) {
        throw new Error(response?.message || "Saved screening run was not found.");
      }
      setLiveRun(response.data);
      setIsRunning(!isTerminalRunStatus(response.data.status));
      setPollCountdown(isTerminalRunStatus(response.data.status) ? 0 : RUN_POLL_INTERVAL_SECONDS);
      setRunStatus(`Loaded saved screening run. ${buildRunStatusMessage(response.data)}`);
    } catch (error: any) {
      setRunStatus(error?.message || "Unable to load saved screening run.");
    } finally {
      setIsLoadingHistoryRun(false);
    }
  };

  // Poll the live run until it reaches a terminal status -- same cadence as Screening.tsx.
  useEffect(() => {
    if (!realRunId || !liveRun || isTerminalRunStatus(liveRun.status)) {
      return undefined;
    }

    setPollCountdown(RUN_POLL_INTERVAL_SECONDS);

    const pollRun = async () => {
      try {
        const refreshed = await api.Screening.getRunAsync(realRunId);
        if (refreshed?.data) {
          setLiveRun(refreshed.data);
          setRunStatus(buildRunStatusMessage(refreshed.data));
          if (isTerminalRunStatus(refreshed.data.status)) {
            setIsRunning(false);
            setPollCountdown(0);
            loadRunHistory();
          } else {
            setPollCountdown(RUN_POLL_INTERVAL_SECONDS);
          }
        }
      } catch (error: any) {
        setRunStatus(error?.message || "Unable to refresh screening status.");
        setPollCountdown(RUN_POLL_INTERVAL_SECONDS);
      }
    };

    const countdownIntervalId = window.setInterval(() => {
      setPollCountdown((current) => (current <= 1 ? RUN_POLL_INTERVAL_SECONDS : current - 1));
    }, 1000);
    const intervalId = window.setInterval(pollRun, 15000);
    return () => {
      window.clearInterval(countdownIntervalId);
      window.clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.Screening, liveRun, realRunId]);

  const agenticChecklist = useMemo(() => {
    const stages = liveRun?.stages || [];
    const traces = liveRun?.agentTraces || [];
    const knownKeys = screeningStageDefinitions.map((stage) => stage.key);
    const dynamicKeys = [
      ...stages.map((stage) => stage.stageKey),
      ...traces.map((trace) => trace.stageKey),
    ]
      .filter((key): key is string => Boolean(key))
      .filter(
        (key, index, keys) =>
          keys.findIndex((candidate) => normalizeStageKey(candidate) === normalizeStageKey(key)) ===
          index
      )
      .filter(
        (key) => !knownKeys.some((knownKey) => normalizeStageKey(knownKey) === normalizeStageKey(key))
      );
    const orderedKeys = [...knownKeys, ...dynamicKeys];

    return orderedKeys.map((key) => {
      const stage = findStageByKey(stages, key);
      const trace = findTraceByKey(traces, key);
      const definition = screeningStageDefinitions.find(
        (item) => normalizeStageKey(item.key) === normalizeStageKey(key)
      );
      const currentStageMatches = normalizeStageKey(liveRun?.currentStage) === normalizeStageKey(key);
      const status = stage?.status || (currentStageMatches ? "Running" : "Pending");
      const stageDetail = stage ? parseStageReason(stage) || parseOutputDetail(stage.outputJson) : null;
      const traceDetail = trace ? parseOutputDetail(trace.outputJson) : null;
      const detail = [stageDetail, traceDetail]
        .filter((item): item is string => Boolean(item))
        .filter((item, index, items) => items.indexOf(item) === index)
        .join("\n\n");

      return {
        key,
        label: definition?.label || humanizeStageKey(key),
        status,
        detail,
        hasDetail: Boolean(detail),
      };
    });
  }, [liveRun]);

  const allChecklistItemsExpanded =
    agenticChecklist.length > 0 &&
    agenticChecklist.every((item) => expandedStageKeys.includes(item.key));

  // Mirrors buildHydratedPropertyCharacteristics in Screening.tsx via the same shared
  // buildScreeningCharacteristics, sourced from the current (possibly edited) form fields
  // instead of a fetched IPropertyContract.
  const buildSandboxCharacteristics = () => {
    const hvacLabels = (formData.hvacPrimaryTypeIds || [])
      .map((id) => hvacOptions.find((option) => option.dataKey === id)?.display)
      .filter((label): label is string => Boolean(label));

    return buildScreeningCharacteristics({
      units: formData.units,
      floors: formData.floors,
      hvacLabels,
      roofSize: formData.roofSize,
      bbl: formData.bbl,
      bin: formData.bin,
      utilityIds: formData.utilityIds,
    });
  };

  // Builds the same intakeText/intakeJson shape the portfolio Screening flow sends (see
  // buildScreeningIntake in Screening.tsx), from this screen's own current form state -- so a
  // sandbox re-run reflects unsaved edits in its narrative just like it already does in
  // propertyProfileOverride.
  const buildSandboxIntake = () => {
    const address = [formData.address, formData.city, formData.state, formData.zip]
      .filter(Boolean)
      .join(", ");
    const utility = [formData.electricUtilityProvider, formData.gasUtilityProvider]
      .filter((name) => {
        const v = (name ?? "").trim().toLowerCase();
        return v && v !== "none" && v !== "n/a" && v !== "na" && v !== "no piped gas";
      })
      .join(" / ");
    const espmSelection = resolveEspmSelectionFromIds(
      espmCategories,
      formData.espmPropertyTypeCategoryId,
      formData.espmPropertyTypeDetailId
    );
    const yearBuilt = formData.yearBuilt.trim() || null;
    const buildingAge = yearBuilt ? `${new Date().getFullYear() - Number(yearBuilt)}` : null;
    const trimmedLastRetrofitYear = lastRetrofitYear.trim() || null;

    return buildScreeningIntake({
      source: "screening-sandbox",
      mode: "existing",
      address,
      companyId: formData.companyId ?? null,
      companyName,
      utility: utility || null,
      electricProvider: formData.electricUtilityProvider || null,
      gasProvider: formData.gasUtilityProvider || null,
      propertyType: espmSelection.propertyType || null,
      espmPropertyTypeCategoryId: formData.espmPropertyTypeCategoryId ?? null,
      espmPropertyTypeDetailId: formData.espmPropertyTypeDetailId ?? null,
      // The matching facts, from this screen's own form state -- so a sandbox
      // run screens the same building the portfolio flow would, including
      // unsaved edits. They reach the form because IPropertyContract now
      // carries them (IMPL-002 task F); before that they were only in
      // Common__PropertySettings and a sandbox run screened with owner class
      // unknown, which flags every incentive with an authored applicant list
      // instead of matching or excluding it.
      buildingUses: formData.buildingUses ?? [],
      ownerClass: formData.ownerClass || null,
      isHistoric: triStateFactToBoolean(formData.isHistoric),
      isAffordableHousing: triStateFactToBoolean(formData.isAffordableHousing),
      isIncomeQualified: triStateFactToBoolean(formData.isIncomeQualified),
      isPrimaryResidence: triStateFactToBoolean(formData.isPrimaryResidence),
      isBarn: triStateFactToBoolean(formData.isBarn),
      squareFeet: formData.sqft.trim() || null,
      buildingAge,
      yearBuilt,
      lastRetrofitYear: trimmedLastRetrofitYear,
      economicObjectives: economicObjectives.trim() || null,
      optionalCharacteristics: buildSandboxCharacteristics() || null,
      propertyId,
      propertySnapshot: {
        propertyId,
        propertyName: formData.propertyName,
        address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        squareFeet: formData.sqft ? Number(formData.sqft) : null,
        units: formData.units ? Number(formData.units) : null,
        floors: formData.floors ? Number(formData.floors) : null,
        buildingAge,
        yearBuilt: yearBuilt ? Number(yearBuilt) : null,
        lastRetrofitYear: trimmedLastRetrofitYear,
        roofSize: formData.roofSize ? Number(formData.roofSize) : null,
        bbl: formData.bbl || null,
        bin: formData.bin || null,
        utilityIds: formData.utilityIds,
        electricUtilityProvider: formData.electricUtilityProvider || null,
        gasUtilityProvider: formData.gasUtilityProvider || null,
      },
    });
  };

  const runScreening = async () => {
    if (!propertyId) {
      setRunStatus("No property selected. Open this from the Testing Sandbox tab.");
      return;
    }

    const valid = await formValidate(async () => {
      setIsRunning(true);
      setLiveRun(null);
      setRunStatus("Creating screening run...");
      try {
        // The override payload IS the same IPropertyContract shape the real Save flow builds --
        // it is only ever sent to Screening/Runs, never to Property/update or Property/create.
        const overridePayload = mapPropertyFormToContract(formData, formData.companyId, propertyId);
        const { intakeText, intakeJson } = buildSandboxIntake();
        const created = await api.Screening.createRunAsync({
          serviceProviderId: 1,
          propertyId,
          intakeText,
          intakeJson: JSON.stringify(intakeJson),
          propertyProfileOverride: overridePayload,
          documentReferences: [],
        });

        if (created?.zeosStatus && created.zeosStatus.toLowerCase() !== "success") {
          throw new Error(created.message || "Unable to create screening run.");
        }

        const createdRunId = created?.data?.runId ?? created?.data?.screeningRunId;
        if (!createdRunId) {
          throw new Error(created?.message || "Run creation did not return an id.");
        }

        setLiveRun(created.data);
        setRunStatus("Queued screening. Starting agentic screening...");
        loadRunHistory();

        const started = await api.Screening.startRunAsync(createdRunId);
        if (started?.data) {
          setLiveRun(started.data);
          setRunStatus(
            `${buildRunStatusMessage(started.data)} Checking every ${RUN_POLL_INTERVAL_SECONDS} seconds.`
          );
          if (isTerminalRunStatus(started.data.status)) {
            setIsRunning(false);
            setPollCountdown(0);
            loadRunHistory();
          } else {
            setPollCountdown(RUN_POLL_INTERVAL_SECONDS);
          }
        }
      } catch (error: any) {
        setRunStatus(error?.message || "Unable to run screening.");
        setIsRunning(false);
        setPollCountdown(0);
      }
    });

    if (!valid) {
      setRunStatus("Fix the highlighted fields before running screening.");
    }
  };

  const resolveReportFileName = (contentDisposition: string | undefined, format: ReportFormat) => {
    const fallback = `${realRunId}-screening-report.${format}`;
    if (!contentDisposition) return fallback;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
    return match?.[1] ? decodeURIComponent(match[1]) : fallback;
  };

  const downloadReportBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  // QT-10 ask 2 (Protocol §9): a static template, not derived from any run -- no API call needed.
  const exportDefectLogTemplate = () => {
    const severityLadder: [string, string][] = [
      [
        "P1",
        "Launch blocker. Wrong program recommended for the geography or utility; a closed program presented as actionable; silent data inference; a fabricated program; non-deterministic output; crash or failed save.",
      ],
      [
        "P2",
        "Fix before launch if possible. Missing conflict flag, missing exclusion reason, confidence not downgraded on missing data, composition changes not affecting output.",
      ],
      ["P3", "Post-launch. Copy, formatting, ordering, and non-blocking UI polish."],
    ];
    const columns = [
      "ID (QT-<date>-<seq>)",
      "Tester",
      "Build / Version",
      "Test ID",
      "Severity (P1/P2/P3)",
      "Property (QA-StoneMill-<initials>)",
      "Field Changed",
      "From",
      "To",
      "Expected",
      "Observed",
      "Reproducible (Yes / No / Intermittent n-of-3)",
      "Run Duration",
    ];
    const toCsvRow = (cells: string[]) =>
      cells.map((cell) => `"${(cell ?? "").replace(/"/g, '""')}"`).join(",");

    const lines = [
      toCsvRow(["Severity Ladder (Protocol §9)"]),
      ...severityLadder.map(([severity, description]) => toCsvRow([severity, description])),
      "",
      toCsvRow(columns),
      toCsvRow([
        "QT-2026-09-14-01",
        "(example row - delete before use)",
        "",
        "T-B1",
        "P2",
        "QA-StoneMill-AB",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ]),
    ];

    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    downloadReportBlob(blob, "qt-defect-log-template.csv");
  };

  const exportBuildingProfile = async () => {
    if (!realRunGuid) {
      setExportError("Run a screening before exporting the building profile.");
      return;
    }

    try {
      setExportError(null);
      const response = await api.Screening.getIncentiveMatchRequestByGuidAsync(realRunGuid);
      if (response?.zeosStatus && response.zeosStatus.toLowerCase() !== "success") {
        throw new Error(response.message || "Unable to load the building profile.");
      }
      if (!response?.data) {
        throw new Error("Building profile export returned no data.");
      }
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: "application/json",
      });
      downloadReportBlob(blob, `${realRunGuid}-building-profile.json`);
    } catch (error: any) {
      setExportError(error?.message || "Building profile export failed.");
    }
  };

  const exportProgramDecisions = async () => {
    if (!realRunId) {
      setExportError("Run a screening before exporting program decisions.");
      return;
    }

    try {
      setExportError(null);
      // Refetch rather than trust the polled liveRun state, so an export mid-run isn't missing a
      // stage that just completed.
      const response = await api.Screening.getRunAsync(realRunId);
      if (response?.zeosStatus && response.zeosStatus.toLowerCase() !== "success") {
        throw new Error(response.message || "Unable to load the screening run.");
      }
      const run = response?.data;
      if (!run) {
        throw new Error("Program decisions export returned no data.");
      }

      const stages = (run.agentTraces || []).map((trace) => {
        let decisions: unknown[] = [];
        if (trace.programDecisionsJson) {
          try {
            decisions = JSON.parse(trace.programDecisionsJson);
          } catch {
            decisions = [];
          }
        }
        return {
          stageKey: trace.stageKey,
          model: trace.model,
          usedOpenAi: trace.usedOpenAi,
          decisions,
        };
      });

      const payload = {
        runId: run.runId ?? run.screeningRunId,
        runGuid: run.runGuid,
        generatedAtUtc: new Date().toISOString(),
        stages,
      };

      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      downloadReportBlob(blob, `${run.runGuid ?? realRunId}-program-decisions.json`);
    } catch (error: any) {
      setExportError(error?.message || "Program decisions export failed.");
    }
  };

  const exportOutputIntegrity = async () => {
    if (!realRunId) {
      setExportError("Run a screening before exporting output integrity checks.");
      return;
    }

    try {
      setExportError(null);
      const response = await api.Screening.getOutputIntegrityAsync(realRunId);
      if (response?.zeosStatus && response.zeosStatus.toLowerCase() !== "success") {
        throw new Error(response.message || "Unable to load output integrity checks.");
      }
      if (!response?.data) {
        throw new Error("Output integrity export returned no data.");
      }

      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: "application/json",
      });
      downloadReportBlob(blob, `${response.data.runGuid ?? realRunId}-output-integrity.json`);
    } catch (error: any) {
      setExportError(error?.message || "Output integrity export failed.");
    }
  };

  const exportReport = async (format: ReportFormat) => {
    if (!realRunId) {
      setExportError("Run a screening before exporting a report.");
      return;
    }

    try {
      setExportError(null);
      const apiFormat = toApiReportFormat(format);
      const generated = await api.Screening.generateReportAsync(realRunId, {
        format: apiFormat,
        reportType: "screening-summary",
        notifyWhenReady: true,
      });
      if (generated?.zeosStatus && generated.zeosStatus.toLowerCase() !== "success") {
        throw new Error(generated.message || "The report event could not be recorded.");
      }

      const download = await api.Screening.downloadReportAsync(
        realRunId,
        "screening-summary",
        apiFormat
      );
      if (download?.zeosStatus && download.zeosStatus.toLowerCase() !== "success") {
        throw new Error("Report download failed.");
      }
      if (!download.rawResponse) {
        throw new Error("Report download returned no file.");
      }

      const contentType =
        (download.rawResponse.headers?.["content-type"] as string) ||
        (format === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
      const blob = new Blob([download.rawResponse.data], { type: contentType });
      const fileName = resolveReportFileName(
        download.rawResponse.headers?.["content-disposition"],
        format
      );
      downloadReportBlob(blob, fileName);
    } catch (error: any) {
      setExportError(error?.message || "Report download failed.");
    }
  };

  const openIncentiveMatching = () => {
    if (!realRunGuid) {
      setRunStatus("Run a screening before opening Incentive Matching.");
      return;
    }

    // Opens in a new tab rather than navigating away -- this screen's unsaved edits would
    // otherwise be lost, defeating the point of a fast change-and-recheck loop.
    const params = new URLSearchParams();
    if (formData.companyId) {
      params.set("company", formData.companyId.toString());
    }
    params.set("screeningRun", realRunGuid);
    window.open(`/incentives?${params.toString()}`, "_blank", "noopener");
  };

  if (!propertyId) {
    return (
      <div className="flex flex-col min-h-screen">
        <DashboardHeader />
        <main className="bg-zeos-white-md p-6 flex-grow">
          <div className="mx-auto max-w-2xl rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-700">
            No property selected. Open this screen from Settings &rarr; Administration &rarr;
            Testing Sandbox by clicking &quot;Open Screening Sandbox&quot; on a property tile.
          </div>
        </main>
        <DashboardFooter />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <DashboardHeader />
      <main className="bg-zeos-white-md p-4 flex-grow md:p-6">
        <div className="mx-auto max-w-6xl space-y-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Screening Sandbox</h1>
            <p className="text-sm text-gray-600">
              Edit any field below and run screening -- nothing here is saved to the property
              record. Every run is a distinct, comparable record.
            </p>
          </div>

          {loadError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {loadError}
            </div>
          )}

          <div className="grid grid-cols-12 gap-4 xl:gap-6">
            <section className="col-span-12 lg:col-span-7 rounded-lg border border-slate-200 bg-white p-4">
              <h2 className="text-base font-semibold text-slate-950 mb-3">
                Property profile (not saved)
              </h2>
              {isLoadingProperty ? (
                <p className="text-sm text-slate-600">Loading property...</p>
              ) : (
                <div className="grid grid-cols-12 gap-y-3">
                  <PropertyForm
                    formData={formData}
                    errors={errors}
                    handleChange={formChange}
                    setFormData={setFormData}
                    hvacOptions={hvacOptions}
                    priorWorkTypeOptions={priorWorkTypeOptions}
                    showServingUtilitySelectors
                    showMatchingFacts
                    showSubmitButton={false}
                    disabled={isRunning}
                  />
                  <div className="col-span-6 px-2">
                    <ZeosInput
                      name="lastRetrofitYear"
                      label="Last Retrofit Year (QA only, not saved)"
                      type="number"
                      value={lastRetrofitYear}
                      onChange={(e) => setLastRetrofitYear(e.target.value)}
                      disabled={isRunning}
                    />
                  </div>
                  <div className="col-span-12 px-2">
                    <ZeosLabel label="Economic Objectives (QA only, not saved)" htmlFor="sandboxEconomicObjectives" />
                    <textarea
                      id="sandboxEconomicObjectives"
                      name="economicObjectives"
                      rows={2}
                      value={economicObjectives}
                      onChange={(e) => setEconomicObjectives(e.target.value)}
                      disabled={isRunning}
                      className="px-4 py-2 w-full border border-zeos-grey-md bg-white text-lg text-zeos-black font-light font-sans focus:outline-none focus:ring-2 focus:ring-zeos-blue transition-all duration-150"
                    />
                  </div>
                </div>
              )}

              {runStatus && (
                <div
                  className={`mt-4 rounded-md border p-2 text-sm ${
                    /failed|unable|error|did not/i.test(runStatus)
                      ? "border-red-200 bg-red-50 text-red-800"
                      : "border-slate-200 bg-slate-50 text-slate-700"
                  }`}
                >
                  {runStatus}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-md bg-zeos-orange-dk px-4 py-1.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-70"
                  onClick={runScreening}
                  disabled={isRunning || isLoadingProperty}
                >
                  {isRunning && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {isRunning ? "Running Screening..." : liveRun ? "Re-run Screening" : "Run Screening"}
                </button>
                {reportFormats.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => exportReport(option.value)}
                    disabled={!realRunId}
                  >
                    {option.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={exportBuildingProfile}
                  disabled={!realRunGuid}
                  title="Downloads the exact pipeline input for this run -- attach it to a defect report instead of a screenshot (Protocol §11 ask 4)."
                >
                  Export Building Profile (JSON)
                </button>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={exportProgramDecisions}
                  disabled={!realRunId}
                  title="Downloads every per-program admit/reject decision recorded across screening stages, with the reason each stage gave (Protocol §11 ask 5)."
                >
                  Export Program Decisions (JSON)
                </button>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={exportOutputIntegrity}
                  disabled={!realRunId}
                  title="Downloads the Protocol §7 output integrity checks (Program Summary Table/TOTAL reconciliation, closed/legacy leakage, excluded-programs reasons, value ranges, confidence, stacking notes, run duration) for this run. Sandbox-only, does not affect the PDF/DOCX export."
                >
                  Export Output Integrity (JSON)
                </button>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-400"
                  onClick={exportDefectLogTemplate}
                  title="Downloads a blank defect log template (Protocol §9 fields + the P1/P2/P3 severity ladder) as a CSV -- not tied to any run."
                >
                  Export Defect Log Template (CSV)
                </button>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={openIncentiveMatching}
                  disabled={!realRunGuid}
                >
                  Open Incentive Matching
                </button>
              </div>
              {exportError && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                  {exportError}
                </div>
              )}
            </section>

            <aside className="col-span-12 lg:col-span-5 space-y-4">
              {liveRun && <ScreeningRunHeader run={liveRun} />}

              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h2 className="text-base font-semibold text-slate-950">Run History</h2>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <p className="text-xs text-slate-500">
                    {liveRun ? `Current run: ${liveRun.status}` : "No run yet."}
                  </p>
                  <button
                    type="button"
                    className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-slate-400"
                    onClick={loadRunHistory}
                    disabled={isHistoryLoading}
                  >
                    {isHistoryLoading ? "Loading..." : "Refresh"}
                  </button>
                </div>
                {historyStatus && (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                    {historyStatus}
                  </div>
                )}
                <div className="mt-2 max-h-64 space-y-2 overflow-y-auto pr-1">
                  {runHistory.map((item) => {
                    const isSelected = item.runGuid === realRunGuid;
                    return (
                      <button
                        key={item.runId}
                        type="button"
                        className={`w-full rounded-md border p-2 text-left text-xs ${
                          isSelected
                            ? "border-zeos-orange-dk bg-orange-50 text-slate-950"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                        onClick={() => loadHistoryRun(item.runGuid)}
                        disabled={isLoadingHistoryRun}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-slate-800">{item.title}</span>
                          <span className="text-slate-500">{item.status}</span>
                        </div>
                        <div className="mt-1 text-slate-500">
                          {item.completedStageCount}/{item.totalStageCount} stages &middot;{" "}
                          {new Date(item.createdAtUtc).toLocaleString()}
                        </div>
                      </button>
                    );
                  })}
                  {runHistory.length === 0 && (
                    <p className="text-xs text-slate-500">No prior runs for this property yet.</p>
                  )}
                </div>
              </div>

              {agenticChecklist.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="text-base font-semibold text-slate-950">Live Agentic Run Output</h2>
                    <button
                      type="button"
                      className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:border-slate-400"
                      onClick={() =>
                        setExpandedStageKeys(
                          allChecklistItemsExpanded ? [] : agenticChecklist.map((item) => item.key)
                        )
                      }
                    >
                      {allChecklistItemsExpanded ? "Collapse all" : "Expand all"}
                    </button>
                  </div>
                  <div className="mt-3 divide-y divide-slate-200 overflow-hidden rounded-md border border-slate-200 bg-white">
                    {agenticChecklist.map((item) => {
                      const isExpanded = expandedStageKeys.includes(item.key);
                      const tone = getChecklistStatusTone(item.status);
                      return (
                        <div key={item.key} className={tone.row}>
                          <button
                            type="button"
                            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                            onClick={() =>
                              setExpandedStageKeys((current) =>
                                current.includes(item.key)
                                  ? current.filter((key) => key !== item.key)
                                  : [...current, item.key]
                              )
                            }
                            aria-expanded={isExpanded}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <span className={`h-2.5 w-2.5 flex-none rounded-full ${tone.dot}`} />
                              <span className="text-sm font-semibold text-slate-950">{item.label}</span>
                            </span>
                            <span
                              className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${tone.badge}`}
                            >
                              {item.status}
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="border-t border-slate-200 bg-white px-3 pb-3 pt-2">
                              <div className="whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-6 text-slate-700">
                                {item.detail || "No result detail has been returned for this step yet."}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};
